import { Backdrop, CircularProgress, StyledEngineProvider, ThemeProvider, Tooltip, Zoom } from '@mui/material';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import axios, { AxiosError } from 'axios';
import React, { useEffect } from 'react';
import { useSelector} from 'react-redux';
import { useAppDispatch } from './business/root-store';
import { actions } from './business/toastr-shim';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { EditProfilePage } from '../src/components/organisms/EditProfilePage/EditProfilePageContainer';
import { PatreonListPage } from '../src/components/organisms/PatreonPage/PatreonListPageContainer';
import { PatreonPage } from '../src/components/organisms/PatreonPage/PatreonPageContainer';
import { ProfilePage } from '../src/components/organisms/ProfilePage/ProfilePageContainer';
import TermsAndConditions from '../src/components/organisms/TermsAndConditions/TermsAndConditions';
import TermsAndConditionsPatreon from '../src/components/organisms/TermsAndConditions/TermsAndConditionsPatreon';
import { routes } from './business/router/path-definitions';
import { navigationAction } from './business/router/router-actions';
import { FetchPatreons } from './business/Static/StaticEpics';
import { getPatreons } from './business/Static/StaticSelectors';
import { ExceptionHandlingResultModel } from './client';
import ScrollToTop from './components/atoms/ScrollToTop/ScrollToTop';
import Footer from './components/molecules/Footer/Footer';
import {
	CustomFab,
	CustomTheme
} from './components/molecules/StyledMaterialUiComponents/StyledMaterialUiComponents';
import AddArticlePage from './components/organisms/AddArticlePage/AddArticlePage';
import ArticlePage from './components/organisms/ArticlePage/ArticlePage';
import { ArticlesByTagPage } from './components/organisms/ArticlesByTagPage/ArticlesByTagPage';
import EditArticlePage from './components/organisms/EditArticlePage/EditArticlePage';
import LandingPage from './components/organisms/LandingPage/LandingPage';
import LoginRegisterPage from './components/organisms/LoginRegisterPage/LoginRegisterPage';
import PaymentsSuccess from './components/organisms/Payments/PaymentsSuccess';
import ProblemPage from './components/organisms/ProblemPage/ProblemPage';
import VerifyMailPage from './components/organisms/VerifyMailPage/VerifyMailPage';
import { VerifyMailPageLogic } from './components/organisms/VerifyMailPage/VerifyMailPageLogic';

export const App: React.FC = () => {
	const patreons = useSelector(getPatreons);
	const dispatch = useAppDispatch();
	useEffect(() => {
		dispatch(FetchPatreons());
	}, [patreons]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [fab, setFab] = React.useState(true);
	const fabChange = () => {
		const windowsScrollTop = window.scrollY;
		if (windowsScrollTop > 800) {
			setFab(false);
		} else {
			setFab(true);
		}
	};
	window.addEventListener('scroll', fabChange);

	var numberOfAjaxCAllPending = 0;
	axios.interceptors.request.use(
		function (config) {
			numberOfAjaxCAllPending++;
			setIsLoading(true);
			return config;
		},
		function (error) {
			return Promise.reject(error);
		}
	);

	axios.interceptors.response.use(
		function (response) {
			numberOfAjaxCAllPending--;
			if (numberOfAjaxCAllPending == 0) {
				setIsLoading(false);
			}
			return response;
		},
		function (reason: AxiosError<{ additionalInfo: string }>) {
			numberOfAjaxCAllPending--;
			if (numberOfAjaxCAllPending == 0) {
				setIsLoading(false);
			}

			if (reason.response!.status === 401) {
				dispatch(
					actions.add({
						message: 'Zaloguj się, aby wykonać tę akcję',
						type: 'warning'
					})
				);
				dispatch(navigationAction.open(routes.login));
			} else {
				var res = reason.response!.data as ExceptionHandlingResultModel;
				if (
					res !== null &&
					res !== undefined &&
					res.errors != null &&
					res.errors != undefined &&
					res.errors.length > 0
				) {
					res.errors.forEach((element) => {
						dispatch(
							actions.add({
								message: element.message ?? '',
								type: 'error'
							})
						);
					});
				} else {
					dispatch(
						actions.add({
							message: 'Wystąpil problem ' + res?.errors,
							type: 'error'
						})
					);
				}
			}

			return Promise.reject();
		}
	);

	return (
		<StyledEngineProvider injectFirst>
		<ThemeProvider theme={CustomTheme}>
			<StylesThemeProvider theme={CustomTheme}>
			<BrowserRouter>
				<Backdrop open={isLoading}>
					<CircularProgress color="inherit" />
				</Backdrop>
				<ScrollToTop />
				<Routes>
					<Route path={routes.home} element={<LandingPage />} />
					<Route path={routes.addArticle} element={<AddArticlePage />} />
					<Route path={routes.editArticle} element={<EditArticlePage />} />
					<Route path={routes.article} element={<ArticlePage />} />
					<Route path={routes.articlesByTag} element={<ArticlesByTagPage />} />
					<Route path={routes.editProfile} element={<EditProfilePage />} />
					<Route path={routes.profile} element={<ProfilePage />} />
					<Route path={routes.patreon} element={<PatreonPage />} />
					<Route path={routes.patreonList} element={<PatreonListPage />} />
					<Route path={routes.paymentsSuccess} element={<PaymentsSuccess />} />
					<Route path={routes.login} element={LoginRegisterPage(0)} />
					<Route path={routes.register} element={LoginRegisterPage(1)} />
					<Route path={routes.verifyMail} element={<VerifyMailPageLogic />} />
					<Route path={routes.verifyMailPlease} element={<VerifyMailPage />} />
					<Route
						path={routes.regulaminPatreon}
						element={<TermsAndConditionsPatreon />}
					/>
					<Route path={routes.regulamin} element={<TermsAndConditions />} />
					<Route path="/404" element={<ProblemPage />} />
					<Route path="*" element={<Navigate to="/404" replace />} />
				</Routes>

				<Zoom in={!fab} unmountOnExit>
					<CustomFab
						size="large"
						onClick={() => {
							window.scrollTo(0, 0);
						}}
						disabled={fab}
					>
						<Tooltip title="Wróć na górę" placement="top">
							<KeyboardArrowUpIcon style={{ fill: 'white' }} />
						</Tooltip>
					</CustomFab>
				</Zoom>
				<Footer />
			</BrowserRouter>
			</StylesThemeProvider>
		</ThemeProvider>
		</StyledEngineProvider>
	);
};
